import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';

export type ModalAccessibilityContainerProps = PropsWithChildren<{
    style?: StyleProp<ViewStyle>;
    accessibilityLabel?: string;
    active?: boolean;
}>;

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Background hiding is refcounted at module scope rather than per-effect so restore is order-independent.
// Accepted trade-off: while modals stack, the first modal's portal is not additionally hidden from screen
// readers (we sweep the body only on the 0->1 transition). This prioritizes never leaving the app root
// stuck aria-hidden/inert after the last modal closes (the per-effect snapshot left it permanently hidden
// when modals closed out of order).
let activeModalCount = 0;
const hiddenElements: { el: HTMLElement; ariaHidden: string | null; inert: boolean }[] = [];

export function ModalAccessibilityContainer(props: ModalAccessibilityContainerProps) {
    const { children, style, accessibilityLabel, active = true } = props;
    const ref = useRef<View>(null);

    useEffect(() => {
        if (!active) {
            return;
        }

        const node = ref.current as unknown as HTMLElement | null;
        if (!node) {
            return;
        }

        const previouslyFocused = (
            typeof document !== 'undefined' ? document.activeElement : null
        ) as HTMLElement | null;

        const getFocusable = () =>
            Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(el => el.offsetParent !== null);

        // Initial focus assumes focusable content is mounted synchronously. Content rendered later becomes tabbable via getFocusable() on keydown, but won't receive initial focus.
        const initial = getFocusable();
        (initial[0] ?? node).focus();

        // react-native-web portals the Modal (and react-native-modal's clickable backdrop) into a div
        // appended to document.body, so we hide siblings only at the body level to keep the backdrop interactive.
        let portalRoot: HTMLElement = node;
        while (portalRoot.parentElement && portalRoot.parentElement !== document.body) {
            portalRoot = portalRoot.parentElement;
        }

        // Invariant: the `if (!active) return;` guard above must stay ABOVE this increment.
        // Consumers like ModalPicker toggle `active` rather than unmounting, so an increment
        // that isn't matched by a balancing cleanup decrement would drift the count.
        if (activeModalCount++ === 0) {
            Array.from(document.body.children).forEach(child => {
                if (child === portalRoot || child.tagName === 'SCRIPT' || child.tagName === 'STYLE') {
                    return;
                }
                const el = child as HTMLElement;
                hiddenElements.push({
                    el,
                    ariaHidden: el.getAttribute('aria-hidden'),
                    inert: el.hasAttribute('inert')
                });
                el.setAttribute('aria-hidden', 'true');
                el.setAttribute('inert', '');
            });
        }

        const handler = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') {
                return;
            }

            const items = getFocusable();
            if (items.length === 0) {
                e.preventDefault();
                node.focus();
                return;
            }

            const first = items[0];
            const last = items[items.length - 1];
            const activeElement = document.activeElement;

            if (e.shiftKey && activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && activeElement === last) {
                e.preventDefault();
                first.focus();
            } else if (!node.contains(activeElement)) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handler, true);

        return () => {
            document.removeEventListener('keydown', handler, true);

            // Clamp at <= 0 (not === 0): Fast Refresh can re-evaluate this module and reset
            // activeModalCount to 0 while old effect cleanups still decrement, driving it negative
            // so === 0 never fires and the background stays hidden until reload.
            if (--activeModalCount <= 0) {
                activeModalCount = 0;
                hiddenElements.forEach(({ el, ariaHidden, inert }) => {
                    if (ariaHidden === null) {
                        el.removeAttribute('aria-hidden');
                    } else {
                        el.setAttribute('aria-hidden', ariaHidden);
                    }
                    if (!inert) {
                        el.removeAttribute('inert');
                    }
                });
                hiddenElements.length = 0;
            }

            previouslyFocused?.focus?.();
        };
    }, [active]);

    return (
        <View
            ref={ref}
            role="dialog"
            aria-modal={active ? true : undefined}
            aria-label={accessibilityLabel}
            accessibilityLabel={accessibilityLabel}
            tabIndex={-1}
            style={style}
        >
            {children}
        </View>
    );
}
